/**
 * Meetings, against the service.
 *
 * **The list and the detail are different shapes on purpose.** `GET
 * /meetings/history` answers with everything a row renders — including
 * `notes_count`, `shared_notes_count` and `share_count` — while the notes
 * themselves and the access list are separate endpoints. Putting them on every
 * list item would cost two extra round trips per row to render three numbers
 * the list already sends, so `Meeting` carries the counts and `MeetingDetail`
 * is fetched per meeting, when a row expands or its share dropdown opens.
 *
 * **Two filters are applied here rather than by the server.** `query` and
 * `spaces` map onto real parameters; the three people filters
 * (participant / access / shared) have no equivalent, so they are applied to
 * the page after it arrives. That means a page can come back shorter than its
 * limit while more matches exist further down — noted at `listMeetings`, and
 * the honest fix is a server-side parameter rather than paging in a loop here.
 */
import { apiGet, apiSend, currentUserId, isOrgAdmin } from '@/lib/api'
import { DEFAULT_LANGUAGE } from './people'
import type {
  Meeting,
  MeetingAccess,
  MeetingDetail,
  MeetingDomain,
  MeetingFilters,
  MeetingNote,
  MeetingPage,
  Person,
  Platform,
  RecordingMode,
  ShareScope,
  MeetingStatus,
} from './types'

const PAGE_SIZE = 20

/* ---------- The wire ---------- */

interface WireParticipant {
  email?: string | null
  name?: string | null
  display_name?: string | null
  is_internal?: boolean
}

interface WireHistoryMeeting {
  id: string
  title?: string | null
  started_at?: string | null
  ended_at?: string | null
  platform?: string | null
  status: string
  participants?: WireParticipant[]
  calendar_participants?: WireParticipant[]
  is_recurring?: boolean
  has_transcript?: boolean
  has_video?: boolean
  video_url?: string | null
  notes_count?: number
  shared_notes_count?: number
  share_count?: number
  space_assignments?: Array<{ space_id: string }>
  effective_visibility?: string
  meeting_url?: string | null
  can_manage_meeting?: boolean
  can_delete_meeting?: boolean
}

interface WireCalendarMeeting {
  id: string
  /** Present on the org-wide answer: whose calendar this came from. */
  calendar_owners?: Array<{ email?: string | null; display_name?: string | null }>
  title?: string | null
  start_time: string
  end_time: string
  meeting_url?: string | null
  bot_scheduled: boolean
  skipped: boolean
  is_recurring: boolean
  effective_visibility?: string
  calendar_participants?: WireParticipant[]
}

/** Platform, from whatever the service knows — the URL is the only reliable tell. */
function platformOf(value: string | null | undefined, url: string | null | undefined): Platform {
  const haystack = `${value ?? ''} ${url ?? ''}`.toLowerCase()
  if (haystack.includes('zoom')) return 'zoom'
  if (haystack.includes('meet.google') || haystack.includes('gmeet')) return 'meet'
  return 'teams'
}

function person(wire: WireParticipant): Person {
  const email = (wire.email ?? '').toLowerCase()
  return {
    email,
    name: wire.display_name || wire.name || email,
    isInternal: wire.is_internal ?? false,
  }
}

/**
 * The service's many statuses, reduced to the five a row can draw.
 *
 * Anything still moving is `processing` rather than enumerated: the row shows a
 * spinner either way, and a status this list has never heard of is far more
 * likely to be a new in-flight state than a new terminal one.
 */
function statusOf(wire: string): MeetingStatus {
  const value = wire.toLowerCase()
  if (value === 'done' || value === 'completed' || value === 'processed') return 'processed'
  if (value.includes('fail') || value === 'error') return 'failed'
  return 'processing'
}

function fromHistory(wire: WireHistoryMeeting): Meeting {
  const participants = [...(wire.calendar_participants ?? []), ...(wire.participants ?? [])]
    .map(person)
    .filter((entry) => entry.email)
  const seen = new Set<string>()

  return {
    id: wire.id,
    title: wire.title ?? 'Untitled meeting',
    startsAt: wire.started_at ?? new Date(0).toISOString(),
    endsAt: wire.ended_at ?? wire.started_at ?? new Date(0).toISOString(),
    platform: platformOf(wire.platform, wire.meeting_url),
    domain: 'past',
    status: statusOf(wire.status),
    participants: participants.filter((entry) =>
      seen.has(entry.email) ? false : (seen.add(entry.email), true)
    ),
    isRecurring: Boolean(wire.is_recurring),
    // A past meeting was recorded or it would not be here; the three-state
    // control is about what happens next, which only an upcoming meeting has.
    recording: 'meeting',
    noteScope: 'meeting',
    spaceId: wire.space_assignments?.[0]?.space_id ?? null,
    noteCount: wire.notes_count ?? 0,
    sharedNoteCount: wire.shared_notes_count ?? 0,
    shareCount: wire.share_count ?? 0,
    canManage: Boolean(wire.can_manage_meeting),
    canDelete: Boolean(wire.can_delete_meeting),
    hasTranscript: Boolean(wire.has_transcript),
    hasVideo: Boolean(wire.has_video),
    videoUrl: wire.video_url ?? null,
  }
}

function fromCalendar(wire: WireCalendarMeeting): Meeting {
  // The owner is folded in as a participant when the invite list is empty, so
  // an admin looking at someone else's meeting can still tell whose it is.
  const owners = (wire.calendar_owners ?? []).map((owner) => ({
    email: (owner.email ?? '').toLowerCase(),
    name: owner.display_name || owner.email || '',
    isInternal: true,
  }))
  const invited = (wire.calendar_participants ?? []).map(person).filter((p) => p.email)
  const participants = invited.length > 0 ? invited : owners.filter((o) => o.email)
  return {
    id: wire.id,
    title: wire.title ?? 'Untitled meeting',
    startsAt: wire.start_time,
    endsAt: wire.end_time,
    platform: platformOf(null, wire.meeting_url),
    domain: 'upcoming',
    status: 'upcoming',
    participants,
    isRecurring: wire.is_recurring,
    recording: wire.skipped ? 'off' : wire.bot_scheduled ? 'meeting' : 'off',
    noteScope: 'meeting',
    spaceId: null,
    noteCount: 0,
    sharedNoteCount: 0,
    shareCount: 0,
    // An upcoming meeting has nothing recorded to rename or delete yet.
    canManage: false,
    canDelete: false,
    hasTranscript: false,
    hasVideo: false,
    videoUrl: null,
  }
}

/* ---------- What has been seen ---------- */

/**
 * Every meeting this session has loaded, by id.
 *
 * The synchronous readers below (`findMeeting`, the chat's pickers) resolve an
 * id while rendering and cannot await. They answer from here, which is current
 * for anything already on screen and empty for anything that is not — the same
 * bargain as `templatesSync` and `spacesSync`.
 */
const CACHE = new Map<string, Meeting>()

/** Meeting url by id, which the recording endpoints take instead of an id. */
const URLS = new Map<string, string>()

function remember(meetings: Meeting[]): Meeting[] {
  for (const meeting of meetings) CACHE.set(meeting.id, meeting)
  return meetings
}

/* ---------- Filtering the server cannot do ---------- */

function matchesPeople(meeting: Meeting, filters: MeetingFilters): boolean {
  const { participant, access, shared } = filters.people
  if (participant.length > 0) {
    if (!participant.some((email) => meeting.participants.some((p) => p.email === email))) {
      return false
    }
  }
  // `access` and `shared` are answered by the per-meeting access list, which the
  // list endpoint does not carry. Filtering on them would mean a request per row,
  // so a set filter narrows to meetings that have any grant at all and says so
  // in the toolbar rather than silently returning the wrong set.
  if (access.length > 0 || shared.length > 0) {
    if (meeting.shareCount === 0) return false
  }
  return true
}

/* ---------- Reads ---------- */

function historyQuery(cursor: number, filters: MeetingFilters): string {
  const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(cursor) })
  if (filters.query.trim()) params.set('query', filters.query.trim())
  for (const spaceId of filters.spaces) params.append('space_ids', spaceId)
  return params.toString()
}

export async function listMeetings(
  domain: MeetingDomain,
  cursor: number,
  filters: MeetingFilters
): Promise<MeetingPage> {
  if (domain === 'past') {
    const body = await apiGet<{ meetings: WireHistoryMeeting[]; total: number }>(
      `/meetings/history?${historyQuery(cursor, filters)}`
    )
    const items = remember(body.meetings.map(fromHistory)).filter((meeting) =>
      matchesPeople(meeting, filters)
    )
    const next = cursor + body.meetings.length
    return { items, nextCursor: next < body.total ? next : null }
  }

  /**
   * Whose upcoming meetings.
   *
   * An administrator sees the ORGANISATION's, everyone else sees their own.
   * That is what the old dashboard did, and dropping it was a real regression:
   * an admin whose own calendar is not connected saw an empty Upcoming tab
   * while ninety-one meetings sat in the tenant, which reads as the calendar
   * integration being broken rather than as a scoping choice.
   *
   * Both routes answer the same shape. `/meetings/:userId` refuses to describe
   * anyone else's calendar — "you can only access your own data" — so the
   * org-wide view has to come from the admin route rather than by asking for
   * another user.
   */
  const body = isOrgAdmin()
    ? await apiGet<{ meetings: WireCalendarMeeting[] }>('/api/admin/meetings/all-upcoming')
    : await apiGet<{ meetings: WireCalendarMeeting[] }>(
        `/meetings/${encodeURIComponent(currentUserId())}`
      )
  const all = remember(body.meetings.map(fromCalendar))
    .filter((meeting) => matchesPeople(meeting, filters))
    .filter((meeting) => {
      const query = filters.query.trim().toLowerCase()
      if (!query) return true
      return [meeting.title, ...meeting.participants.map((p) => p.name)]
        .join(' ')
        .toLowerCase()
        .includes(query)
    })
    .sort((left, right) => left.startsAt.localeCompare(right.startsAt))

  for (const wire of body.meetings) {
    if (wire.meeting_url) URLS.set(wire.id, wire.meeting_url)
  }

  const items = all.slice(cursor, cursor + PAGE_SIZE)
  const next = cursor + items.length
  return { items, nextCursor: next < all.length ? next : null }
}

/**
 * How many items have to be loaded for `date` to be on screen.
 *
 * For the past list this is a count, not a scan: the number of meetings more
 * recent than the target date *is* its offset, and `total` answers that in one
 * request with `limit=1`.
 */
export async function cursorForDate(
  domain: MeetingDomain,
  date: string,
  filters: MeetingFilters
): Promise<number> {
  if (domain === 'past') {
    const params = new URLSearchParams({ limit: '1', offset: '0' })
    if (filters.query.trim()) params.set('query', filters.query.trim())
    for (const spaceId of filters.spaces) params.append('space_ids', spaceId)
    // Exclusive of the target day, so the day itself lands at the top.
    params.set('started_after', `${date}T23:59:59.999Z`)
    const body = await apiGet<{ total: number }>(`/meetings/history?${params.toString()}`)
    return body.total
  }

  const page = await listMeetings('upcoming', 0, filters)
  const index = page.items.findIndex((meeting) => toYmd(meeting.startsAt) >= date)
  return index < 0 ? page.items.length : index
}

/** Local-time `YYYY-MM-DD`, which is the key days are grouped and jumped by. */
export function toYmd(value: string | Date): string {
  const date = typeof value === 'string' ? new Date(value) : value
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

export function fromYmd(value: string): Date | null {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return null
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

export function findMeeting(id: string | null): Meeting | undefined {
  if (!id) return undefined
  return CACHE.get(id)
}

/* ---------- One meeting, in full ---------- */

interface WireNote {
  id: string
  note_template_id: string | null
  title: string
  content: string
  shared_externally?: boolean
  prompt?: string | null
}

interface WireAccess {
  effective_visibility?: string
  grants: Array<{ id: string; email: string; preferred_language: string }>
  series_grants: Array<{ id: string; email: string; preferred_language: string }>
  participants: Array<{ email: string; name?: string | null; is_internal?: boolean }>
  directory: Array<{ email: string; name: string; is_internal: boolean }>
  default_language?: string
}

function toAccess(wire: WireAccess, participants: Person[]): MeetingAccess {
  const byEmail = new Map<string, { grant: ShareScope; language: string }>()
  for (const grant of wire.grants) {
    byEmail.set(grant.email.toLowerCase(), {
      grant: 'meeting',
      language: grant.preferred_language || DEFAULT_LANGUAGE,
    })
  }
  // A series grant outranks a meeting one: it is the wider reach, and showing
  // the narrower would understate what the person can already see.
  for (const grant of wire.series_grants) {
    byEmail.set(grant.email.toLowerCase(), {
      grant: 'series',
      language: grant.preferred_language || DEFAULT_LANGUAGE,
    })
  }

  const invited = new Set(participants.map((p) => p.email))
  const known = new Map(wire.directory.map((entry) => [entry.email.toLowerCase(), entry]))
  const emails = new Set([...invited, ...byEmail.keys()])

  return {
    organization: wire.effective_visibility === 'organization' ? 'meeting' : 'none',
    people: [...emails].map((email) => {
      const directory = known.get(email)
      const granted = byEmail.get(email)
      return {
        email,
        name: participants.find((p) => p.email === email)?.name ?? directory?.name ?? email,
        isInternal:
          participants.find((p) => p.email === email)?.isInternal ?? directory?.is_internal ?? false,
        isParticipant: invited.has(email),
        grant: granted?.grant ?? 'none',
        language: granted?.language ?? null,
      }
    }),
  }
}

/** Grant ids, so a revoke knows what to delete. Keyed `meetingId:email`. */
const GRANT_IDS = new Map<string, string>()

/**
 * Meetings whose notes and access have been fetched.
 *
 * Separate from `CACHE` because it answers a different question: `CACHE` knows
 * every meeting that has scrolled past, this knows the few that were opened.
 * The synchronous readers use it as a fallback so a surface that has the detail
 * can render it without a second request, and one that does not renders empty
 * rather than guessing.
 */
const DETAILS = new Map<string, MeetingDetail>()

/** The detail already loaded for a meeting, or undefined. Never fetches. */
export function cachedDetail(id: string | null): MeetingDetail | undefined {
  return id ? DETAILS.get(id) : undefined
}

/** Notes already loaded for a meeting; empty when it has not been opened. */
export function cachedNotes(id: string | null): MeetingNote[] {
  return cachedDetail(id)?.notes ?? []
}

/** Access already loaded for a meeting, or an empty grant list. */
export function cachedAccess(id: string | null): MeetingAccess {
  return cachedDetail(id)?.access ?? { organization: 'none', people: [] }
}

/**
 * A meeting with its notes and its access list.
 *
 * The two requests go together because every surface that wants one wants the
 * other — the detail panel renders notes above the share list, and the share
 * dropdown needs the notes' shared flags to say what is going out.
 *
 * Notes 404 for a meeting the account may read but not *manage*; that is the
 * service's way of not confirming a meeting exists, so it is an empty list here
 * rather than an error the panel would have to explain.
 */
export async function getMeetingDetail(id: string): Promise<MeetingDetail> {
  const base = CACHE.get(id)
  const [notes, access] = await Promise.all([
    apiGet<{ notes: WireNote[] }>(`/api/meetings/${id}/notes`).catch(() => ({
      notes: [] as WireNote[],
    })),
    apiGet<WireAccess>(`/api/access/meetings/${id}`),
  ])

  for (const grant of [...access.grants, ...access.series_grants]) {
    GRANT_IDS.set(`${id}:${grant.email.toLowerCase()}`, grant.id)
  }

  const participants =
    base?.participants ??
    access.participants.map((entry) => ({
      email: entry.email.toLowerCase(),
      name: entry.name ?? entry.email,
      isInternal: entry.is_internal ?? false,
    }))

  const detail: MeetingDetail = {
    ...(base ?? {
      id,
      title: 'Meeting',
      startsAt: new Date(0).toISOString(),
      endsAt: new Date(0).toISOString(),
      platform: 'teams' as Platform,
      domain: 'past' as MeetingDomain,
      status: 'processed' as MeetingStatus,
      participants,
      isRecurring: false,
      recording: 'meeting' as RecordingMode,
      noteScope: 'meeting' as const,
      spaceId: null,
      noteCount: 0,
      sharedNoteCount: 0,
      shareCount: 0,
      canManage: false,
      canDelete: false,
      hasTranscript: false,
      hasVideo: false,
      videoUrl: null,
    }),
    notes: notes.notes.map((note) => ({
      id: note.id,
      templateId: note.note_template_id,
      title: note.title,
      body: note.content ?? '',
      sharedExternally: Boolean(note.shared_externally),
      pending: false,
      prompt: note.prompt ?? null,
    })),
    access: toAccess(access, participants),
  }

  CACHE.set(id, { ...detail, noteCount: detail.notes.length })
  DETAILS.set(id, detail)
  return detail
}

/* ---------- Cross-surface reads ---------- */

function cached(): Meeting[] {
  return [...CACHE.values()]
}

export function meetingsInSpaces(spaceIds: string[], limit = 8): Meeting[] {
  return cached()
    .filter(
      (meeting) =>
        meeting.spaceId !== null &&
        spaceIds.includes(meeting.spaceId) &&
        meeting.status === 'processed'
    )
    .sort((left, right) => right.startsAt.localeCompare(left.startsAt))
    .slice(0, limit)
}

export function selectableMeetings(
  filters: MeetingFilters,
  from: string | null,
  limit = 60
): Meeting[] {
  return cached()
    .filter((meeting) => meeting.domain === 'past' && meeting.status === 'processed')
    .filter((meeting) => matchesPeople(meeting, filters))
    .filter(
      (meeting) =>
        filters.spaces.length === 0 ||
        (meeting.spaceId !== null && filters.spaces.includes(meeting.spaceId))
    )
    .filter((meeting) => !from || toYmd(meeting.startsAt) <= from)
    .sort((left, right) => right.startsAt.localeCompare(left.startsAt))
    .slice(0, limit)
}

export function selectableCount(filters: MeetingFilters, from: string | null): number {
  return selectableMeetings(filters, from, Number.MAX_SAFE_INTEGER).length
}

/**
 * Free-text search over what has been loaded.
 *
 * A placeholder for the service's own search: the backend indexes transcripts
 * and note bodies and ranks them properly, which nothing here can do over a
 * page of titles. This exists so the fixture-backed chat keeps working until it
 * moves onto that endpoint.
 */
export function searchMeetings(query: string, limit = 5, spaceIds?: string[] | null): Meeting[] {
  const terms = query.toLowerCase().split(/\s+/).filter((term) => term.length > 2)
  if (terms.length === 0) return []

  return cached()
    .filter((meeting) => meeting.status === 'processed')
    .filter(
      (meeting) =>
        !spaceIds ||
        spaceIds.length === 0 ||
        (meeting.spaceId !== null && spaceIds.includes(meeting.spaceId))
    )
    .map((meeting) => {
      const title = meeting.title.toLowerCase()
      const people = meeting.participants.map((p) => p.name.toLowerCase()).join(' ')
      let score = 0
      for (const term of terms) {
        if (title.includes(term)) score += 2
        if (people.includes(term)) score += 1
      }
      return { meeting, score }
    })
    .filter((entry) => entry.score > 0)
    .sort(
      (left, right) =>
        right.score - left.score || right.meeting.startsAt.localeCompare(left.meeting.startsAt)
    )
    .slice(0, limit)
    .map((entry) => entry.meeting)
}

export function pickableMeetings(query: string, spaceId: string | null, limit = 30): Meeting[] {
  const normalized = query.trim().toLowerCase()
  return cached()
    .filter((meeting) => meeting.status === 'processed')
    .filter((meeting) => !spaceId || meeting.spaceId === spaceId)
    .filter((meeting) => {
      if (!normalized) return true
      return [meeting.title, ...meeting.participants.map((p) => p.name)]
        .join(' ')
        .toLowerCase()
        .includes(normalized)
    })
    .sort((left, right) => right.startsAt.localeCompare(left.startsAt))
    .slice(0, limit)
}

/* ---------- Mutations ---------- */

/** Re-reads the meeting so the caller renders what was stored, not what was sent. */
async function reread(meetingId: string): Promise<MeetingDetail> {
  return getMeetingDetail(meetingId)
}

/**
 * File a meeting into a space, or unfile it.
 *
 * The service models this as an attachment row on the space rather than a
 * column on the meeting, so moving one is a delete and an insert. The delete
 * needs the attachment's id, which only the space knows — hence the lookup.
 */
export async function setSpace(meetingId: string, spaceId: string | null): Promise<MeetingDetail> {
  const current = CACHE.get(meetingId)?.spaceId ?? null
  if (current === spaceId) return reread(meetingId)

  if (current) {
    const detail = await apiGet<{
      attachments: Array<{ id: string; meeting_id: string | null }>
    }>(`/api/spaces/${current}`)
    const attachment = detail.attachments.find((row) => row.meeting_id === meetingId)
    if (attachment) {
      await apiSend('DELETE', `/api/spaces/${current}/attachments/${attachment.id}`)
    }
  }
  if (spaceId) {
    await apiSend('POST', `/api/spaces/${spaceId}/attachments`, {
      kind: 'meeting',
      meeting_id: meetingId,
    })
  }

  const known = CACHE.get(meetingId)
  if (known) CACHE.set(meetingId, { ...known, spaceId })
  return reread(meetingId)
}

/**
 * Whether an upcoming meeting is recorded, and how far that decision reaches.
 *
 * The endpoints take the meeting's URL rather than its id, because a calendar
 * event is identified by where it happens — the same URL is how the recorder
 * finds it later.
 */
export async function setRecording(
  meetingId: string,
  recording: RecordingMode
): Promise<MeetingDetail> {
  const url = URLS.get(meetingId)
  if (!url) throw new Error('This meeting has no link to record')
  const user = encodeURIComponent(currentUserId())
  const query = `?meeting_url=${encodeURIComponent(url)}`

  if (recording === 'off') await apiGet(`/meetings/${user}/skip${query}`)
  else if (recording === 'series') await apiGet(`/meetings/${user}/series/enable${query}`)
  else await apiGet(`/meetings/${user}/enable${query}`)

  const known = CACHE.get(meetingId)
  if (known) CACHE.set(meetingId, { ...known, recording })
  return reread(meetingId)
}

/** Open the meeting to the whole tenant, or close it back to its participants. */
export async function setOrganizationAccess(
  meetingId: string,
  scope: ShareScope
): Promise<MeetingDetail> {
  await apiSend('PATCH', `/meetings/history/${meetingId}/visibility`, {
    visibility: scope === 'none' ? 'private' : 'organization',
  })
  return reread(meetingId)
}

/**
 * Grant or revoke one person.
 *
 * `language` is required to grant and meaningless to revoke, because granting
 * sends mail and revoking does not.
 */
export async function setPersonAccess(
  meetingId: string,
  email: string,
  grant: ShareScope,
  language?: string
): Promise<MeetingDetail> {
  if (grant === 'none') {
    const grantId = GRANT_IDS.get(`${meetingId}:${email.toLowerCase()}`)
    if (grantId) await apiSend('DELETE', `/api/access/meetings/${meetingId}/${grantId}?scope=meeting`)
  } else {
    await apiSend('POST', `/api/access/meetings/${meetingId}`, {
      email,
      language: language ?? DEFAULT_LANGUAGE,
      scope: grant,
    })
  }
  return reread(meetingId)
}

/**
 * Grant every external attendee who does not already have access.
 *
 * One request per person, because the service grants one address at a time and
 * each grant sends its own mail. They go together so a slow mailer does not
 * make this feel like a queue.
 */
export async function shareWithAllParticipants(
  meetingId: string,
  scope: Exclude<ShareScope, 'none'>,
  language: string
): Promise<MeetingDetail> {
  const detail = await getMeetingDetail(meetingId)
  const targets = detail.access.people.filter(
    (entry) => !entry.isInternal && entry.isParticipant && entry.grant === 'none'
  )
  await Promise.all(
    targets.map((entry) =>
      apiSend('POST', `/api/access/meetings/${meetingId}`, {
        email: entry.email,
        language,
        scope,
      })
    )
  )
  return reread(meetingId)
}

export async function shareWithEmails(
  meetingId: string,
  emails: string[],
  scope: Exclude<ShareScope, 'none'>,
  language: string
): Promise<MeetingDetail> {
  await Promise.all(
    emails.map((email) =>
      apiSend('POST', `/api/access/meetings/${meetingId}`, { email, language, scope })
    )
  )
  return reread(meetingId)
}

export async function addPerson(
  meetingId: string,
  email: string,
  scope: ShareScope,
  language: string = DEFAULT_LANGUAGE
): Promise<MeetingDetail> {
  if (scope === 'none') return reread(meetingId)
  return shareWithEmails(meetingId, [email], scope, language)
}

/**
 * Rename a meeting.
 *
 * The title is the only thing about a past meeting anyone edits: it arrives
 * from the calendar or from the recorder's own client, and both produce
 * "Teams call – 2026-07-24 21:13 UTC" often enough that a list of them is
 * unreadable.
 */
export async function renameMeeting(meetingId: string, title: string): Promise<MeetingDetail> {
  await apiSend('PATCH', `/meetings/history/${meetingId}`, { title: title.trim() })
  const known = CACHE.get(meetingId)
  if (known) CACHE.set(meetingId, { ...known, title: title.trim() })
  return reread(meetingId)
}

/**
 * Delete a meeting, with its recording, transcript and notes.
 *
 * Whether it is allowed at all, and by whom, is the organisation's deletion
 * policy — the server enforces it and answers 403 when the answer is no, which
 * is why nothing is checked here beyond having asked.
 */
export async function deleteMeeting(meetingId: string): Promise<void> {
  await apiSend('DELETE', `/meetings/history/${meetingId}`)
  CACHE.delete(meetingId)
  DETAILS.delete(meetingId)
}

/* ---------- Notes ---------- */

export async function addNote(meetingId: string, templateId: string): Promise<MeetingDetail> {
  await apiSend('POST', `/api/meetings/${meetingId}/notes/generate`, {
    note_template_id: templateId,
  })
  return reread(meetingId)
}

export async function addBlankNote(meetingId: string, title: string): Promise<MeetingDetail> {
  await apiSend('POST', `/api/meetings/${meetingId}/notes/manual`, { title, content: '' })
  return reread(meetingId)
}

/**
 * A note written from a one-off instruction rather than a saved template.
 *
 * NOT WIRED — `POST /notes/generate` takes a template id and nothing else, so
 * there is no route that runs an ad-hoc prompt against a transcript. Failing
 * loudly beats writing the prompt into an empty note and calling it done.
 */
export function addPromptNote(_meetingId: string, _prompt: string): Promise<MeetingDetail> {
  return Promise.reject(
    new Error('Running a one-off prompt needs a service endpoint that does not exist yet.')
  )
}

export async function updateNote(
  meetingId: string,
  noteId: string,
  patch: Partial<Pick<MeetingNote, 'title' | 'body' | 'sharedExternally'>>
): Promise<MeetingDetail> {
  if (patch.sharedExternally !== undefined) {
    await apiSend('PATCH', `/api/meetings/${meetingId}/notes/${noteId}/sharing`, {
      shared: patch.sharedExternally,
    })
  }
  if (patch.title !== undefined || patch.body !== undefined) {
    await apiSend('PUT', `/api/meetings/${meetingId}/notes/${noteId}`, {
      ...(patch.title === undefined ? {} : { title: patch.title }),
      ...(patch.body === undefined ? {} : { content: patch.body }),
    })
  }
  return reread(meetingId)
}

export async function removeNote(meetingId: string, noteId: string): Promise<MeetingDetail> {
  await apiSend('DELETE', `/api/meetings/${meetingId}/notes/${noteId}`)
  return reread(meetingId)
}

/**
 * Which templates run on an upcoming meeting, and whether that choice covers
 * the series.
 *
 * NOT WIRED. The service decides what runs from the template's own
 * `auto_generate` flag, org-wide; there is no per-meeting selection to write
 * to, and no per-series note scope at all. Both need a service change.
 */
export function toggleTemplate(_meetingId: string, _templateId: string): Promise<MeetingDetail> {
  return Promise.reject(
    new Error('Choosing notes per meeting needs a service endpoint that does not exist yet.')
  )
}

export function setNoteScope(
  _meetingId: string,
  _scope: 'meeting' | 'series'
): Promise<MeetingDetail> {
  return Promise.reject(
    new Error('Note scope is not something the service stores yet.')
  )
}

/* ---------- Transcript ---------- */

/**
 * The transcript, as text.
 *
 * Fetched rather than derived: the service holds it, and the detail panel wants
 * it only when someone asks to copy or download.
 */
export async function meetingTranscript(meeting: Meeting): Promise<string> {
  const body = await apiGet<{ transcript?: string | null }>(
    `/meetings/history/${encodeURIComponent(meeting.id)}`
  )
  return body.transcript ?? ''
}
